import { addMoney } from '@shared/lib/money';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Money } from '../../../../types';
import { RunServiceRequest, RunServiceRequestPassengers, Segment } from '../../../types';
import { get, SEATS_RECOMMENDATION_SHOWN, set } from '../../../../cache';
import { fillOrder } from '../../../store/order/actions';
import { MotivationGoal, reachGoal } from '../../../../analytics';
import { Seat } from '../../../../SeatMap/types';
import { useMediaQuery } from 'react-responsive';
import { getCssVariablePx, TABLET_MIDDLE_WIDTH } from '../../../../utils';
import {
	FlightSeatMapFragment,
	PassengerFragment,
	PreselectedSeatFragment,
	SelectedSeatFragment
} from '@websky/graphql';

interface Props {
	confirmRecommendedSeatsWithoutDialog: boolean;
	orderId: string;
	segments: Segment[];
	travellers: PassengerFragment[];
	runServiceRequest?: RunServiceRequest;
	setSeat?: (seat: Seat[], passengerId: string, segmentId: string) => void;
	fillOrder: typeof fillOrder;
	onClose?: () => void;
	segmentIndex?: number;
	passengerId?: string;
	isEnabled?: boolean;
	seatMap: FlightSeatMapFragment;
}

interface SegmentRow {
	price: Money;
	seats: {
		[passengerId: string]: PreselectedSeatFragment;
	};
	seatsMap?: {
		[number: string]: PreselectedSeatFragment['seat'];
	};
}

interface RowsMap {
	[segmentId: string]: SegmentRow;
}

interface SeatsRecommendContextType {
	isOpen?: boolean;
	isEnabled?: boolean;
	preselectedSeatsByPassengers?: PreselectedSeatFragment[][];
	onConfirm?: () => void;
	onDisagree?: () => void;
	rowsMap?: RowsMap;
	onOpen?: () => void;
	availableSeatsCount?: number;
}

export const SeatsRecommendContext = React.createContext<SeatsRecommendContextType>({});

const RECOMMENDED_SEAT_TOP_OFFSET = 100;

const SeatsRecommendation: React.FC<Props> = ({
	confirmRecommendedSeatsWithoutDialog,
	orderId,
	travellers,
	segments,
	runServiceRequest,
	setSeat,
	fillOrder,
	children,
	segmentIndex,
	passengerId,
	isEnabled,
	seatMap
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const rowsMap: RowsMap = {};

	const isMobile = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });
	const seatTopOffset = isMobile ? 0 : RECOMMENDED_SEAT_TOP_OFFSET + getCssVariablePx('--wx-custom-header-height');

	const firstSegmentId = segments?.[0].id;
	const firstPreselected = travellers?.[0]?.preselectedServices?.seats?.find(
		seat => seat.segment.id === firstSegmentId
	)?.seat;

	const onOpen = () => {
		setIsOpen(true);
		set(`${SEATS_RECOMMENDATION_SHOWN}_${orderId}_${segmentIndex}`, true);
	};

	useEffect(() => {
		if (confirmRecommendedSeatsWithoutDialog) {
			onConfirm();
		}
	}, [confirmRecommendedSeatsWithoutDialog]);

	useEffect(() => {
		setIsOpen(false);
		if (
			!(
				get(`${SEATS_RECOMMENDATION_SHOWN}_${orderId}_${segmentIndex}`) ||
				get(`${SEATS_RECOMMENDATION_SHOWN}_${orderId}_ALL`)
			) &&
			isEnabled &&
			!confirmRecommendedSeatsWithoutDialog
		) {
			if (travellers.some(traveller => traveller.preselectedServices.seats.length)) {
				onOpen();
			}
		}
	}, [isEnabled, travellers]);

	const needToScrollToRecommendedSeat =
		typeof confirmRecommendedSeatsWithoutDialog === 'boolean' && confirmRecommendedSeatsWithoutDialog;

	const onDisagree = useCallback(() => {
		setIsOpen(false);

		reachGoal(MotivationGoal.RefuseProposedSeat);
	}, []);

	const onConfirm = useCallback(() => {
		const seat: HTMLButtonElement = document.querySelector(`[data-seat-number="${firstPreselected?.number}"]`);

		setIsOpen(false);

		if (runServiceRequest) {
			const request: RunServiceRequestPassengers[] = travellers.map(({ preselectedServices, id }) => {
				return {
					passengerId: id,
					seats: preselectedServices.seats.map(
						({ seat, segment }): SelectedSeatFragment => ({
							isConfirmed: false,
							isChangeable: true,
							onRequest: false,
							row: parseInt(seat.row),
							letter: seat.letter,
							seat: null, // wtf is this
							segment,
							type: null,
							product: null
						})
					)
				};
			});

			runServiceRequest(request, fillOrder);
		} else if (setSeat) {
			travellers.map(({ preselectedServices, id }) => {
				preselectedServices.seats
					.filter(it => {
						if (confirmRecommendedSeatsWithoutDialog) {
							return true;
						} else {
							return it.segment.id === segmentIndex.toString();
						}
					})
					.map(seat => {
						setSeat(
							[
								{
									number: seat.seat.number,
									row: seat.seat.row,
									price: seat.seat.price,
									letter: seat.seat.letter,
									service: null,
									rfisc: null,
									isAvailable: null,
									isAisle: null,
									isExistent: null,
									isSmoking: null,
									isRecline: null,
									seatService: null,
									seatServices: null,
									services: [],
									restrictions: null,
									permissions: []
								}
							],
							id,
							seat.segment.id
						);
					});
			});
		}

		if (firstPreselected && seat && window.hasOwnProperty('scrollTo') && needToScrollToRecommendedSeat) {
			window.scrollTo({
				top: seat.getBoundingClientRect().top - seatTopOffset + window.scrollY,
				behavior: 'smooth'
			});
		}

		if (confirmRecommendedSeatsWithoutDialog) {
			set(`${SEATS_RECOMMENDATION_SHOWN}_${orderId}_ALL`, true);
		}

		reachGoal(MotivationGoal.ConfirmProposedSeat);
	}, [
		travellers,
		fillOrder,
		confirmRecommendedSeatsWithoutDialog,
		needToScrollToRecommendedSeat,
		segmentIndex,
		seatTopOffset,
		firstPreselected
	]);

	travellers?.forEach(({ preselectedServices, id: passengerId }) =>
		preselectedServices.seats.forEach(seat => {
			const id = seat.segment.id;

			if (rowsMap[id]) {
				rowsMap[id].price = addMoney(rowsMap[id].price, seat.seat.price);
				rowsMap[id].seats[passengerId] = seat;
				rowsMap[id].seatsMap[seat.seat.number] = seat.seat;
			} else {
				rowsMap[id] = {
					price: { ...seat.seat.price },
					seats: {
						[passengerId]: seat
					},
					seatsMap: {
						[seat.seat.number]: seat.seat
					}
				};
			}
		})
	);

	const scrollToPreselectedSeat = () => {
		const seat = rowsMap[segmentIndex.toString()]?.seats?.[passengerId];

		if (seat?.seat) {
			const seatDOM: HTMLButtonElement = document.querySelector(`[data-seat-number="${seat.seat.number}"]`);

			window.scrollTo({
				top: seatDOM.getBoundingClientRect().top - seatTopOffset + window.scrollY,
				behavior: 'smooth'
			});
		}
	};

	React.useEffect(() => {
		if (isOpen) {
			setTimeout(scrollToPreselectedSeat);
		}
	}, [segmentIndex, passengerId, isOpen]);

	return (
		<SeatsRecommendContext.Provider
			value={{
				isOpen,
				onOpen,
				onConfirm,
				onDisagree,
				rowsMap: rowsMap,
				isEnabled,
				availableSeatsCount: seatMap.segments[segmentIndex].availableSeatsCount
			}}
		>
			{children}
		</SeatsRecommendContext.Provider>
	);
};

export default SeatsRecommendation;
